import React, { useState } from "react";
import {
  Box,
  TextField,
  Autocomplete,
} from "@mui/material";

interface GradientBorderAutocompleteProps {
  options: string[];
  inputValue: string;
  onInputChange: (event: any, newInputValue: string) => void;
  label: string;
  width?: number;
  autocomplete?: string;
}

const GradientBorderAutocomplete: React.FC<GradientBorderAutocompleteProps> = ({
  options,
  inputValue,
  onInputChange,
  label,
  width = 1500,
  autocomplete,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  // インラインSVGで色を動的に制御
  const DynamicSVGBorder = () => (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 670 104"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      <defs>
        <linearGradient
          id={`borderGradient-${isFocused ? "focused" : "normal"}`}
          x1="0%"
          y1="0%"
          x2="0%"
          y2="100%"
        >
          <stop
            offset="0%"
            style={{
              stopColor: isFocused ? "#a8a8d4" : "#a8a8d4", // フォーカス時は元の色、非フォーカス時は灰色
            }}
          />
          <stop
            offset="100%"
            style={{
              stopColor: isFocused ? "#e3ff7f" : "#e3ff7f", // フォーカス時は元の色、非フォーカス時は灰色
            }}
          />
        </linearGradient>
      </defs>
      <rect
        x="1"
        y="1"
        width="658"
        height="100"
        fill={"#ffffff"} // 背景色を常に白に統一
        stroke={`url(#borderGradient-${isFocused ? "focused" : "normal"})`}
        strokeWidth="6"
        rx="22"
      />
    </svg>
  );

  return (
    <Box sx={{ position: "relative", width, height: 60 }}>
      {/* 動的SVGボーダー */}
      <DynamicSVGBorder />

      {/* Autocompleteを上に配置 */}
      <Autocomplete
        freeSolo
        disableClearable
        options={options}
        inputValue={inputValue}
        onInputChange={onInputChange}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 1,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={label}
            size="small"
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            inputProps={{
              ...params.inputProps,
              autoComplete: autocomplete,
              spellCheck: false, // スペルチェック無効化
            }}
            sx={{
              width: "100%",
              height: "100%",
              "& .MuiOutlinedInput-root": {
                height: "100%",
                padding: "12px", // Add padding to contain the autofill highlight
                "& fieldset": {
                  border: "none",
                },
                "&:hover fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  border: "none",
                },
                backgroundColor: "transparent",
                borderRadius: "4px",
              },
              "& .MuiInputBase-input": {
                padding: "8px 40px 8px 12px", // パスワード欄とpaddingを合わせる
                fontSize: "16px",
                // 万が一オートフィルが動作した場合の最小限の対応
                "&:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
                  WebkitTextFillColor: "#000 !important",
                  backgroundColor: "transparent !important",
                },
                "&:-webkit-autofill:hover": {
                  WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
                  WebkitTextFillColor: "#000 !important",
                  backgroundColor: "transparent !important",
                },
                "&:-webkit-autofill:focus": {
                  WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
                  WebkitTextFillColor: "#000 !important",
                  backgroundColor: "transparent !important",
                },
                "&:-webkit-autofill:active": {
                  WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
                  WebkitTextFillColor: "#000 !important",
                  backgroundColor: "transparent !important",
                },
                "&::placeholder": {
                  color: isFocused ? "#999" : "#bbb", // プレースホルダーの色も変更
                  opacity: 1,
                },
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default GradientBorderAutocomplete;